/**
 * Login Component
 * 
 * Provides a login form for application users to authenticate.
 * Displays error messages for failed login attempts.
 * Includes a help button to access documentation.
 * 
 * @param onSuccess - Callback function called after successful login
 */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { API_BASE_URL } from '../constants';
import Help from './Help';
import ForgotPassword from './ForgotPassword';
import './Login.css';

interface LoginProps {
  onSuccess?: () => void;
}

const Login: React.FC<LoginProps> = ({ onSuccess }) => {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [backendVersion, setBackendVersion] = useState<string | null>(null);
  const [frontendVersion, setFrontendVersion] = useState<string>('2.1.0');
  const [frontendBuildDate, setFrontendBuildDate] = useState<string | null>(null);
  const [frontendBuildTime, setFrontendBuildTime] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Fetch available years and backend version on mount (before login)
  useEffect(() => {
    const fetchAvailableYears = async () => {
      try {
        // Try to get years without auth (might fail, but that's okay)
        const response = await axios.get(`${API_BASE_URL}/api/auth/available-years`);
        if (response.data.success && response.data.years) {
          const years = response.data.years;
          setAvailableYears(years);
        }
      } catch (error) {
        // If not authenticated, that's fine - we'll get years after login
      }
    };
    
    const fetchBackendVersion = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/version`);
        if (response.data.success && response.data.version) {
          setBackendVersion(response.data.version);
        }
      } catch (error) {
        // If backend is not available, that's okay - version will remain null
      }
    };
    
    const fetchFrontendVersion = async () => {
      try {
        // Try to load version.json from build directory
        const response = await axios.get('/version.json');
        if (response.data && response.data.version) {
          setFrontendVersion(response.data.version);
          if (response.data.buildDate) {
            setFrontendBuildDate(response.data.buildDate);
          }
          if (response.data.buildTime) {
            setFrontendBuildTime(response.data.buildTime);
          }
        }
      } catch (error) {
        // Fallback: try to get from package.json (if available)
        try {
          const packageJson = require('../../package.json');
          if (packageJson.version) {
            setFrontendVersion(packageJson.version);
          }
        } catch (e) {
          // Keep default version
        }
      }
    };
    
    fetchAvailableYears();
    fetchBackendVersion();
    fetchFrontendVersion();
  }, []);

  // Set German validation messages
  useEffect(() => {
    if (usernameRef.current) {
      usernameRef.current.setCustomValidity('');
      usernameRef.current.oninvalid = (e) => {
        e.preventDefault();
        (e.target as HTMLInputElement).setCustomValidity('Bitte fülle dieses Feld aus');
      };
    }
    if (passwordRef.current) {
      passwordRef.current.setCustomValidity('');
      passwordRef.current.oninvalid = (e) => {
        e.preventDefault();
        (e.target as HTMLInputElement).setCustomValidity('Bitte fülle dieses Feld aus');
      };
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Always use the latest year (first in the array, which is sorted DESC)
      const latestYear = availableYears.length > 0 ? availableYears[0] : undefined;
      await login(username, password, latestYear);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  // Show forgot password screen if requested
  if (showForgotPassword) {
    return <ForgotPassword onBack={() => setShowForgotPassword(false)} />;
  }

  return (
    <div className="login-wrapper">
      <div className="login-container">
        <button
          className="login-help-button"
          onClick={() => setShowHelp(true)}
          title="Hilfe & Anleitung anzeigen"
        >
          ?
        </button>
        <img src="/roemerman-logo.png" alt="RömerMan Logo" className="login-logo" />
        <h1 className="login-title">Helfermanagement</h1>
        <h2 className="login-subtitle">Römerman {availableYears.length > 0 ? availableYears[0] : 2026}</h2>
        <div className="login-version-info">
          Frontend v{frontendVersion}
          {frontendBuildDate && frontendBuildTime && ` (${frontendBuildDate} ${frontendBuildTime})`}
          {backendVersion && ` • Backend v${backendVersion}`}
        </div>
        <form onSubmit={handleSubmit} className="login-form">
          <div className="login-form-group">
            <label htmlFor="username" className="login-label">
              Benutzername
            </label>
            <input
              ref={usernameRef}
              id="username"
              type="text"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                e.target.setCustomValidity('');
              }}
              className="login-input"
              required
              autoComplete="username"
              disabled={loading}
            />
          </div>
          <div className="login-form-group">
            <label htmlFor="password" className="login-label">
              Passwort
            </label>
            <input
              ref={passwordRef}
              id="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                e.target.setCustomValidity('');
              }}
              className="login-input"
              required
              autoComplete="current-password"
              disabled={loading}
            />
          </div>
          {error && <div className="login-error">{error}</div>}
          <button
            type="submit"
            className="login-button"
            disabled={loading || !username || !password}
          >
            {loading ? 'Anmeldung...' : 'Anmelden'}
          </button>
          <div style={{ textAlign: 'center', marginTop: '15px' }}>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#ed1c24',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontFamily: 'inherit',
                padding: '5px'
              }}
              disabled={loading}
            >
              Passwort vergessen?
            </button>
          </div>
        </form>
      </div>
      {showHelp && (
        <Help onClose={() => setShowHelp(false)} />
      )}
    </div>
  );
};

export default Login;

